(function () {
  "use strict";

  var DEFAULT_API_BASE = "https://reg.mainstevents.com";
  var AUTOSAVE_DELAY_MS = 2000;
  var FIRST_TOUCH_KEY = "mse_first_touch";
  var CLIENT_ID_KEY = "mse_client_id";
  var CLICK_ID_KEYS = ["gclid", "msclkid", "fbclid", "wbraid", "gbraid", "ttclid"];
  var firstTouchCache = null;
  var lastTouchSnapshot = null;
  var clientIdCache = null;
  var EXTRAS_SOURCE = "shopify_registration_v1";
  var EVENT_ID_KEY_PREFIX = "mse_event_id_";
  // Verification: run document.querySelector(".flair-reg")?.dataset?.apiBase to confirm the configured base URL.

  function safeGetItem(key) {
    try {
      return localStorage.getItem(key);
    } catch (err) {
      return null;
    }
  }

  function safeSetItem(key, value) {
    try {
      localStorage.setItem(key, value);
    } catch (err) {
      /* ignore */
    }
  }

  function safeSessionGetItem(key) {
    try {
      return sessionStorage.getItem(key);
    } catch (err) {
      return null;
    }
  }

  function safeSessionSetItem(key, value) {
    try {
      sessionStorage.setItem(key, value);
    } catch (err) {
      /* ignore */
    }
  }

  function safeSessionRemoveItem(key) {
    try {
      sessionStorage.removeItem(key);
    } catch (err) {
      /* ignore */
    }
  }

  function normalizeEmail(value) {
    if (value == null) return null;
    var trimmed = String(value).trim().toLowerCase();
    return trimmed || null;
  }

  function toNullableString(value) {
    if (value == null) return null;
    var str = String(value).trim();
    return str ? str : null;
  }

  function ensureObject(value) {
    if (!value) return {};
    if (typeof value === "string") {
      try {
        var parsed = JSON.parse(value);
        return ensureObject(parsed);
      } catch (err) {
        return {};
      }
    }
    if (typeof value !== "object" || Array.isArray(value)) return {};
    return value;
  }

  var DEBUG_PII_GUARD = false;
  try {
    DEBUG_PII_GUARD = new URLSearchParams(window.location.search).get("debug_pii") === "1";
  } catch (err) {
    DEBUG_PII_GUARD = false;
  }

  var DATALAYER_ALLOWLIST = [
    "event",
    "event_id",
    "registration_id",
    "show_code",
    "program",
    "registration_type",
    "page_path",
    "traffic_channel",
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_content",
    "utm_term",
    "fbp",
    "fbc",
    "fbclid",
    "gclid",
    "wbraid",
    "gbraid",
    "msclkid",
    "ttclid",
    "client_id",
    "session_token",
    "visitor_id",
    "external_id",
    "meta_standard_event",
    "google_conversion_event",
    "lead_type",
    "lead_origin",
    "step",
    "em",
    "ph",
    "fn",
    "ln",
    "ct",
    "st",
    "zp",
    "country"
  ];

  var RAW_PII_KEYS = [
    "user_data",
    "email",
    "phone",
    "first_name",
    "last_name",
    "city",
    "region",
    "postal_code",
    "address1",
    "address2",
    "country"
  ];
  var RAW_PII_KEY_SET = {};
  for (var rpk = 0; rpk < RAW_PII_KEYS.length; rpk++) RAW_PII_KEY_SET[RAW_PII_KEYS[rpk]] = true;

  function looksHashedValue(key, value) {
    if (typeof value !== "string") return false;
    var loweredKey = String(key || "").toLowerCase();
    var aliasMap = {
      email: "em",
      phone: "ph",
      first_name: "fn",
      last_name: "ln",
      city: "ct",
      region: "st",
      postal_code: "zp",
      country: "country"
    };
    var hashedKey = Object.prototype.hasOwnProperty.call(aliasMap, loweredKey) ? aliasMap[loweredKey] : loweredKey;
    var hashedKeys = { em: true, ph: true, fn: true, ln: true, ct: true, st: true, zp: true, country: true };
    if (!hashedKeys[hashedKey]) return false;
    return /^[0-9a-f]{64}$/i.test(value);
  }

  function pick(obj, keys) {
    var out = {};
    if (!obj) return out;
    for (var i = 0; i < keys.length; i++) {
      var key = keys[i];
      if (Object.prototype.hasOwnProperty.call(obj, key)) out[key] = obj[key];
    }
    return out;
  }

  function containsRawPII(value) {
    if (!value || typeof value === "function") return false;
    if (typeof value !== "object") return false;
    if (Array.isArray(value)) {
      for (var i = 0; i < value.length; i++) {
        if (containsRawPII(value[i])) return true;
      }
      return false;
    }
    for (var key in value) {
      if (!Object.prototype.hasOwnProperty.call(value, key)) continue;
      var lowerKey = String(key).toLowerCase();
      if (RAW_PII_KEY_SET[lowerKey]) {
        if (!looksHashedValue(lowerKey, value[key])) return true;
      }
      if (containsRawPII(value[key])) return true;
    }
    return false;
  }

  function sanitizeForDataLayer(eventPayload) {
    if (!eventPayload) return null;
    var safe = pick(eventPayload, DATALAYER_ALLOWLIST);
    if (safe.user_data) delete safe.user_data;
    for (var safeKey in safe) {
      if (!Object.prototype.hasOwnProperty.call(safe, safeKey)) continue;
      var safeVal = safe[safeKey];
      var type = typeof safeVal;
      var isPrimitive =
        safeVal === null || type === "string" || type === "number" || type === "boolean";
      if (!isPrimitive) delete safe[safeKey];
    }
    var hadRaw = containsRawPII(eventPayload);
    var safeStillSuspicious = containsRawPII(safe);
    if (safeStillSuspicious) {
      if (hadRaw && DEBUG_PII_GUARD) {
        console.warn("[FLAIR] dataLayer event dropped (PII)", {
          event: eventPayload.event || null,
          event_id: eventPayload.event_id || null
        });
      }
      return null;
    }
    if (hadRaw && DEBUG_PII_GUARD) {
      console.warn("[FLAIR] dataLayer event sanitized", {
        event: eventPayload.event || null,
        event_id: eventPayload.event_id || null
      });
    }
    return Object.keys(safe).length ? safe : null;
  }

  function getQueryParams(search) {
    var params = {};
    var qs = typeof search === "string" ? search : window.location.search;
    if (!qs) return params;
    if (qs.charAt(0) === "?") qs = qs.slice(1);
    if (!qs) return params;
    qs.split("&").forEach(function (pair) {
      if (!pair) return;
      var parts = pair.split("=");
      var key = decodeURIComponent(parts[0] || "").toLowerCase();
      if (!key) return;
      var rawVal = parts[1] || "";
      rawVal = rawVal.replace(/\+/g, " ");
      var value = decodeURIComponent(rawVal);
      params[key] = value;
    });
    return params;
  }

  function readCookie(name) {
    try {
      var match = document.cookie.match(new RegExp("(^|;)\\s*" + name + "=([^;]*)"));
      return match ? decodeURIComponent(match[2]) : "";
    } catch (err) {
      return "";
    }
  }

  function getCookie(name) {
    return readCookie(name);
  }

  function setCookie(name, value, maxAgeSeconds) {
    if (!name) return;
    try {
      var parts = [name + "=" + encodeURIComponent(value || "")];
      parts.push("path=/");
      if (typeof maxAgeSeconds === "number") {
        parts.push("max-age=" + Math.max(0, Math.floor(maxAgeSeconds)));
      }
      parts.push("SameSite=Lax");
      if (window.location && window.location.protocol === "https:") parts.push("Secure");
      document.cookie = parts.join("; ");
    } catch (err) {
      /* ignore */
    }
  }

  function marketingConsentGranted() {
    try {
      if (window.Cookiebot && Cookiebot.consent && typeof Cookiebot.consent.marketing === "boolean") {
        return !!Cookiebot.consent.marketing;
      }
    } catch (err) {
      /* ignore */
    }
    return true;
  }

  function normalizeName(value) {
    if (value == null) return null;
    var trimmed = String(value).trim().toLowerCase();
    return trimmed || null;
  }

  function normalizePhone(value) {
    if (value == null) return null;
    var digits = String(value).replace(/\D+/g, "");
    return digits || null;
  }

  function sha256Hex(value) {
    if (!value) return Promise.resolve("");
    try {
      if (!window.crypto || !crypto.subtle || typeof TextEncoder === "undefined") return Promise.resolve("");
      var encoder = new TextEncoder();
      var data = encoder.encode(value);
      return crypto.subtle.digest("SHA-256", data).then(function (hashBuffer) {
        var bytes = new Uint8Array(hashBuffer);
        var hex = "";
        for (var i = 0; i < bytes.length; i++) {
          var h = bytes[i].toString(16);
          if (h.length === 1) h = "0" + h;
          hex += h;
        }
        return hex;
      });
    } catch (err) {
      return Promise.resolve("");
    }
  }

  var ONE_YEAR_SECONDS = 365 * 24 * 60 * 60;
  var NINETY_DAYS_SECONDS = 90 * 24 * 60 * 60;
  var EXTERNAL_ID_STORAGE_KEY = "mse_external_id";

  function persistExternalId(value) {
    if (!value) return;
    try {
      safeSetItem(EXTERNAL_ID_STORAGE_KEY, value);
    } catch (err) {
      /* ignore */
    }
    if (marketingConsentGranted()) setCookie("_extid", value, ONE_YEAR_SECONDS);
  }

  function getStoredExternalId() {
    var stored = safeGetItem(EXTERNAL_ID_STORAGE_KEY);
    if (stored) return stored;
    return null;
  }

  function getCanonicalExternalId() {
    var cookieVal = getCookie("_extid");
    if (cookieVal) {
      var stored = getStoredExternalId();
      if (cookieVal !== stored) {
        try {
          safeSetItem(EXTERNAL_ID_STORAGE_KEY, cookieVal);
        } catch (err) {
          /* ignore */
        }
      }
      return cookieVal;
    }
    var fallback = getStoredExternalId();
    if (fallback) return fallback;
    return null;
  }

  function getOrInitExternalId() {
    var existing = getCanonicalExternalId();
    if (existing) return existing;
    var clientId = getOrInitClientId();
    persistExternalId(clientId);
    return clientId;
  }

  function updateIdentitySnapshot(externalId, firstTouch, lastTouch) {
    try {
      var snapshot = {
        external_id: externalId || getCanonicalExternalId() || getOrInitExternalId(),
        client_id: getOrInitClientId(),
        first_touch_at: (firstTouch && firstTouch.first_touch_at) || null,
        landing_page: (firstTouch && firstTouch.landing_page) || null,
        last_touch_at: (lastTouch && lastTouch.last_touch_at) || null,
        last_touch_page: (lastTouch && lastTouch.last_touch_page) || null,
        last_touch_channel:
          (lastTouch && (lastTouch.last_touch_channel || lastTouch.traffic_channel)) || null
      };
      safeSetItem("mse_identity", JSON.stringify(snapshot));
    } catch (err) {
      /* ignore */
    }
  }

  function prepareHashedIdentityData(payload) {
    payload = payload || {};
    var normalized = {
      em: normalizeEmail(payload.email),
      ph: normalizePhone(payload.phone),
      fn: normalizeName(payload.first_name),
      ln: normalizeName(payload.last_name),
      ct: normalizeName(payload.city),
      st: normalizeName(payload.region),
      zp: payload.postal_code == null ? null : String(payload.postal_code).trim().toLowerCase() || null,
      country: normalizeName(payload.country)
    };
    var keys = Object.keys(normalized);
    var hashing = keys.map(function (key) {
      var value = normalized[key];
      if (!value) return Promise.resolve([key, ""]);
      return sha256Hex(value).then(
        function (hash) {
          return [key, hash || ""];
        },
        function () {
          return [key, ""];
        }
      );
    });
    return Promise.all(hashing)
      .then(function (pairs) {
        var hashes = {};
        pairs.forEach(function (pair) {
          hashes[pair[0]] = pair[1] || "";
        });
        return { hashes: hashes, normalized: normalized };
      })
      .catch(function () {
        var emptyHashes = {};
        keys.forEach(function (key) {
          emptyHashes[key] = "";
        });
        return { hashes: emptyHashes, normalized: normalized };
      });
  }

  function getMetaCookieValues(params) {
    var meta = { fbp: "", fbc: "" };
    var fbp = readCookie("_fbp") || "";
    var fbc = readCookie("_fbc") || "";
    var fbclid = params && params.fbclid ? params.fbclid : "";
    if (!fbc && fbclid) {
      var ts = Math.floor(Date.now() / 1000);
      fbc = "fb.1." + ts + "." + fbclid;
    }
    meta.fbp = fbp;
    meta.fbc = fbc;
    return meta;
  }

  function generateUUID() {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    var random = function () {
      return Math.floor((1 + Math.random()) * 0x10000)
        .toString(16)
        .substring(1);
    };
    return random() + random() + "-" + random() + "-" + random() + "-" + random() + "-" + random() + random() + random();
  }

  function generateEventId() {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    return "REG-" + Date.now() + "-" + Math.random().toString(16).slice(2);
  }

  function getOrInitEventId(showKey) {
    var key = EVENT_ID_KEY_PREFIX + (showKey || "default");
    try {
      var existing = sessionStorage.getItem(key);
      if (existing) return existing;
      var id = generateEventId();
      sessionStorage.setItem(key, id);
      return id;
    } catch (err) {
      return generateEventId();
    }
  }

  function getDeviceType() {
    var width = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth || 0;
    if (width < 768) return "Mobile";
    if (width < 1024) return "Tablet";
    return "Desktop";
  }

  function getTimeZone() {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    } catch (err) {
      return "";
    }
  }

  function getOrInitClientId() {
    if (clientIdCache) return clientIdCache;
    var existing = safeGetItem(CLIENT_ID_KEY);
    if (existing) {
      clientIdCache = existing;
      return existing;
    }
    var newId = generateUUID();
    clientIdCache = newId;
    safeSetItem(CLIENT_ID_KEY, newId);
    return newId;
  }

  function hostnameFromUrl(url) {
    if (!url) return "";
    try {
      return new URL(url).hostname.toLowerCase();
    } catch (err) {
      return "";
    }
  }

  function deriveTrafficChannel(meta, referrer) {
    var medium = (meta.utm_medium || meta.utmMedium || "").toLowerCase();
    var source = (meta.utm_source || meta.utmSource || "").toLowerCase();
    var refHost = hostnameFromUrl(referrer);
    if (meta.gclid || meta.msclkid || meta.wbraid || meta.gbraid) return "Paid Search";
    if (meta.fbclid) return "Paid Social";
    if (medium.indexOf("email") !== -1) return "Email";
    if (["facebook", "instagram", "meta"].indexOf(source) !== -1 || medium.indexOf("paid_social") !== -1) return "Paid Social";
    if (medium.indexOf("cpc") !== -1 || medium.indexOf("ppc") !== -1) return "Paid Search";

    var organicSearchHosts = ["google.", "bing.", "yahoo.", "duckduckgo.", "baidu.", "yandex.", "ecosia."];
    var organicSocialHosts = [
      "facebook.",
      "l.facebook.",
      "lm.facebook.",
      "m.facebook.",
      "instagram.",
      "t.co",
      "pinterest.",
      "linkedin.",
      "youtube."
    ];
    if (organicSearchHosts.some(function (host) { return refHost.indexOf(host) !== -1; })) return "Organic Search";
    if (organicSocialHosts.some(function (host) { return refHost.indexOf(host) !== -1; })) return "Organic Social";
    var currentHost = hostnameFromUrl(window.location && window.location.href);
    if (refHost && refHost === currentHost) return "Direct";
    if (refHost) return "Referral";
    return "Direct";
  }

  function getOrInitFirstTouch() {
    if (firstTouchCache) return firstTouchCache;
    var stored = safeGetItem(FIRST_TOUCH_KEY);
    if (stored) {
      try {
        firstTouchCache = JSON.parse(stored);
        if (firstTouchCache && typeof firstTouchCache === "object") return firstTouchCache;
      } catch (err) {
        /* ignore parse errors */
      }
    }
    var params = getQueryParams();
    var nowIso = new Date().toISOString();
    var ref = document.referrer || "";
    var metaCookies = getMetaCookieValues(params);
    var firstTouch = {
      landing_page: window.location.href,
      referrer: ref,
      first_touch_at: nowIso,
      device_type: getDeviceType(),
      utm_source: params.utm_source || "",
      utm_medium: params.utm_medium || "",
      utm_campaign: params.utm_campaign || "",
      utm_content: params.utm_content || "",
      utm_term: params.utm_term || "",
      fbp: metaCookies.fbp || "",
      fbc: metaCookies.fbc || "",
      client_id: getOrInitClientId(),
      language: navigator.language || "",
      timezone: getTimeZone()
    };
    var channelMeta = {
      utm_source: firstTouch.utm_source,
      utm_medium: firstTouch.utm_medium
    };
    CLICK_ID_KEYS.forEach(function (key) {
      var val = params[key] || "";
      firstTouch[key] = val;
      channelMeta[key] = val;
    });
    firstTouch.traffic_channel = deriveTrafficChannel(channelMeta, ref);
    safeSetItem(FIRST_TOUCH_KEY, JSON.stringify(firstTouch));
    firstTouchCache = firstTouch;
    return firstTouchCache;
  }

  function buildLastTouch() {
    var params = getQueryParams();
    var ref = document.referrer || "";
    var nowIso = new Date().toISOString();
    var metaCookies = getMetaCookieValues(params);
    var extras = {
      last_touch_at: nowIso,
      last_touch_page: window.location.href,
      last_touch_referrer: ref,
      last_touch_utm_source: params.utm_source || "",
      last_touch_utm_medium: params.utm_medium || "",
      last_touch_utm_campaign: params.utm_campaign || "",
      last_touch_utm_content: params.utm_content || "",
      last_touch_utm_term: params.utm_term || "",
      submission_path: window.location.pathname || "",
      submission_at: nowIso,
      language: navigator.language || "",
      timezone: getTimeZone(),
      last_touch_fbp: metaCookies.fbp || "",
      last_touch_fbc: metaCookies.fbc || ""
    };
    var channelMeta = {
      utm_source: params.utm_source,
      utm_medium: params.utm_medium
    };
    CLICK_ID_KEYS.forEach(function (key) {
      var val = params[key] || "";
      extras["last_touch_" + key] = val;
      channelMeta[key] = val;
    });
    extras.last_touch_channel = deriveTrafficChannel(channelMeta, ref);
    lastTouchSnapshot = extras;
    return extras;
  }

  function normalizeApiBase(base) {
    if (!base) return DEFAULT_API_BASE;
    try {
      var resolved = new URL(base, window.location.origin);
      return (resolved.origin + resolved.pathname).replace(/\/$/, "");
    } catch (err) {
      console.warn("FLAIR registration: invalid API base", base, err);
      return DEFAULT_API_BASE;
    }
  }

  function resolveApiBase(root) {
    var base =
      (root && root.dataset && root.dataset.apiBase) ||
      (typeof window !== "undefined" && window.FLAIR_API_BASE) ||
      DEFAULT_API_BASE;

    if (window.location.protocol === "https:" && /^http:\/\//i.test(base)) {
      console.warn("FLAIR registration: refusing insecure API base on https page", base);
    }
    return normalizeApiBase(base);
  }

  function safeStringify(value) {
    try {
      return JSON.stringify(value);
    } catch (err) {
      return "";
    }
  }

  function safeReadJson(res) {
    var contentType = "";
    try {
      if (res && res.headers && typeof res.headers.get === "function") {
        contentType = res.headers.get("content-type") || "";
      }
    } catch (err) {
      contentType = "";
    }
    if (!contentType || contentType.toLowerCase().indexOf("application/json") === -1) {
      return Promise.resolve({});
    }
    try {
      return res.json().catch(function () {
        return {};
      });
    } catch (err) {
      return Promise.resolve({});
    }
  }

  function externalIdStrength(value) {
    if (!value) return 0;
    if (value.indexOf("shopify:") === 0) return 4;
    if (value.indexOf("mse_person:") === 0) return 3;
    if (value.indexOf("mse_reg:") === 0) return 2;
    return 1;
  }

  function getCandidateExternalIds(options) {
    options = options || {};
    var candidates = [];
    var clientId = options.clientId;
    if (clientId) candidates.push(clientId);
    var registrationId = options.registrationId;
    if (registrationId) candidates.push("mse_reg:" + registrationId);
    var body = options.body || {};
    var externalFromBody = toNullableString(body.external_id);
    if (externalFromBody && externalFromBody.indexOf("mse_person:") === 0) candidates.push(externalFromBody);
    var personId = body.person_id || body.user_id || body.customer_id;
    if (personId) candidates.push("mse_person:" + personId);
    var shopifyId = toNullableString(options.shopifyId);
    if (shopifyId) candidates.push("shopify:" + shopifyId);
    return candidates;
  }

  function chooseBestExternalId(current, candidates) {
    var best = current || null;
    var bestStrength = externalIdStrength(best);
    (candidates || []).forEach(function (candidate) {
      if (!candidate) return;
      var strength = externalIdStrength(candidate);
      if (strength > bestStrength) {
        best = candidate;
        bestStrength = strength;
      }
    });
    return best;
  }

  function hasGooglePlaces() {
    return !!(window.google && window.google.maps && window.google.maps.places);
  }

  function loadGooglePlacesOnce(apiKey) {
    if (hasGooglePlaces()) return Promise.resolve();

    var stateKey = "__flairGooglePlacesLoader";
    var state = window[stateKey];
    if (!state || typeof state !== "object") {
      state = { status: "idle", promise: null, script: null };
      window[stateKey] = state;
    }
    if (state.promise) return state.promise;

    state.status = "loading";
    state.promise = new Promise(function (resolve, reject) {
      var settled = false;
      var pollTimer = null;
      var timeoutTimer = null;
      var script = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');

      function cleanup() {
        if (pollTimer) clearInterval(pollTimer);
        if (timeoutTimer) clearTimeout(timeoutTimer);
        if (script) {
          script.removeEventListener("load", handleLoad);
          script.removeEventListener("error", handleError);
        }
      }

      function finishReady() {
        if (settled) return;
        settled = true;
        cleanup();
        state.status = "ready";
        resolve();
      }

      function finishFailed(message) {
        if (settled) return;
        settled = true;
        cleanup();
        state.status = "failed";
        reject(new Error(message));
      }

      function handleLoad() {
        if (hasGooglePlaces()) finishReady();
      }

      function handleError() {
        finishFailed("Google Places failed to load.");
      }

      if (!script) {
        script = document.createElement("script");
        script.src =
          "https://maps.googleapis.com/maps/api/js?key=" +
          encodeURIComponent(apiKey) +
          "&libraries=places&loading=async";
        script.async = true;
        script.defer = true;
        script.setAttribute("data-flair-google-places-loader", "true");
      }

      state.script = script;
      script.addEventListener("load", handleLoad);
      script.addEventListener("error", handleError);

      pollTimer = setInterval(function () {
        if (hasGooglePlaces()) finishReady();
      }, 100);
      timeoutTimer = setTimeout(function () {
        finishFailed("Google Places was unavailable after loading.");
      }, 15000);

      if (!script.parentNode) document.head.appendChild(script);
      if (hasGooglePlaces()) finishReady();
    });

    return state.promise;
  }

  function boot(scope) {
    var roots = (scope || document).querySelectorAll(".flair-reg:not([data-initialized])");
    for (var i = 0; i < roots.length; i++) {
      initOne(roots[i]);
      roots[i].setAttribute("data-initialized", "true");
    }
  }

  function initOne(root) {
    var form = root.querySelector("#flairRegForm");
    if (!form) return;

    /* -------- autosave session setup -------- */
    var apiBase = resolveApiBase(root);
    var showCodeEl = form.elements["show_code"];
    var showSlugEl = form.elements["show_slug"];
    var showCode = showCodeEl ? (showCodeEl.value || "").trim() : "";
    var showSlug = showSlugEl ? (showSlugEl.value || "").trim() : "";
    var showKey = showCode || showSlug || "default";
    var storageKey = "flair_reg_resume_" + showKey;
    var stableEventId = getOrInitEventId(showKey);
    var shopifyCustomerId = toNullableString(root && root.dataset && root.dataset.shopifyCustomerId);
    var latestExternalId = getCanonicalExternalId();

    var resumeToken = safeSessionGetItem(storageKey) || null;
    var autosaveArmed = !!resumeToken;
    var autosaveTimer = null;
    var lastAutosaveSignature = "";
    var submitting = false;
    var debugAttribution = false;
    try {
      debugAttribution = new URLSearchParams(window.location.search).get("debug_attribution") === "1";
    } catch (err) {
      debugAttribution = false;
    }
    if (debugAttribution) console.log("[REG] apiBase resolved", apiBase);

    /* -------- original selectors -------- */
    var progressEl = root.querySelector(".flair-reg__progress");
    var rail = root.querySelector(".flair-reg__progress-rail");
    var bar = root.querySelector(".flair-reg__progress-bar");
    var stepLabelEl = root.querySelector(".flair-reg__step-label");
    var allSteps = Array.prototype.slice.call(root.querySelectorAll(".flair-step"));
    var totalSteps = allSteps.length;
    var formSteps = allSteps.filter(function (step) {
      return !step.classList.contains("flair-step--complete");
    });
    var totalFormSteps = formSteps.length || totalSteps;
    var confirmationStepIndex = totalSteps - 1;
    var currentStepIndex = 0;
    var stepOneTracked = false;

    var attendeesWrap = form.querySelector("[data-attendees]");
    var othersToggle = form.querySelectorAll("input[name='has_others']");
    var othersCountWrap = form.querySelector("[data-others-count]");
    var othersCountEl = form.querySelector("input[name='others_count']");
    var msgEl = root.querySelector("#flairRegMsg");
    var addressInput = form.querySelector("[data-address-autocomplete]");
    var addressHidden = form.querySelector("[data-sync-hidden='address1']");
    var submitBtn = form.querySelector('[type="submit"]');
    var lazyPlacesApiKey = root.getAttribute("data-google-places-api-key") || "";
    var lazyPlacesEnabled = root.getAttribute("data-places-load") === "registration-intent" && !!lazyPlacesApiKey;
    var lazyPlacesPromise = null;
    var placesAttachState = "idle";
    var placesIntentListenersBound = false;

    /* -------- helpers -------- */
    function setMessage(text, variant) {
      if (!msgEl) return;
      msgEl.textContent = text || "";
      msgEl.classList.remove("f-message--error", "f-message--warning");
      if (!text) return;
      if (variant === "error") msgEl.classList.add("f-message--error");
      else if (variant === "warning") msgEl.classList.add("f-message--warning");
    }

    function getHiddenValue(name) {
      var el = form.elements[name];
      return el ? el.value || "" : "";
    }

    function setGeographyState(partial) {
      if (!partial) return;
      var fields = [
        "raw_country",
        "raw_region",
        "country",
        "region",
        "canonical_country_code",
        "canonical_subdivision_code",
        "geography_normalization_status",
        "geography_normalization_issues"
      ];
      for (var f = 0; f < fields.length; f++) {
        var fieldName = fields[f];
        if (!Object.prototype.hasOwnProperty.call(partial, fieldName)) continue;
        if (fieldName === "geography_normalization_issues") {
          var issues = partial[fieldName];
          if (typeof issues === "string") {
            try {
              var parsed = JSON.parse(issues);
              if (Array.isArray(parsed)) issues = parsed;
              else issues = issues ? [issues] : [];
            } catch (err) {
              issues = issues ? [issues] : [];
            }
          } else if (!Array.isArray(issues)) {
            issues = issues ? [String(issues)] : [];
          }
          setHidden(fieldName, JSON.stringify(issues));
          continue;
        }
        var value = partial[fieldName];
        setHidden(fieldName, value == null ? "" : String(value));
      }
    }

    function parseIssues(raw) {
      if (!raw) return [];
      try {
        var parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed
          .map(function (entry) {
            if (entry == null) return null;
            return String(entry).trim();
          })
          .filter(Boolean);
      } catch (err) {
        return [];
      }
    }

    function getGeographyState() {
      var issues = parseIssues(getHiddenValue("geography_normalization_issues"));
      return {
        raw_country: toNullableString(getHiddenValue("raw_country")),
        raw_region: toNullableString(getHiddenValue("raw_region")),
        country: toNullableString(getHiddenValue("country")),
        region: toNullableString(getHiddenValue("region")),
        canonical_country_code: toNullableString(getHiddenValue("canonical_country_code")),
        canonical_subdivision_code: toNullableString(getHiddenValue("canonical_subdivision_code")),
        geography_normalization_status: toNullableString(getHiddenValue("geography_normalization_status")),
        geography_normalization_issues: issues
      };
    }

    function captureRawGeographyFromVisible() {
      var countryDisplay = form.elements["country_display"];
      var regionDisplay = form.elements["region_display"];
      var rawCountryValue = countryDisplay ? countryDisplay.value || "" : "";
      var rawRegionValue = regionDisplay ? regionDisplay.value || "" : "";
      setGeographyState({ raw_country: rawCountryValue, raw_region: rawRegionValue });
    }

    function normalizeBackendGeography(input) {
      if (!input || typeof input !== "object") return null;
      var status = input.geography_normalization_status || input.status || null;
      var issues = input.geography_normalization_issues || input.issues || [];
      var normalizedIssues = Array.isArray(issues)
        ? issues.map(function (issue) {
            if (issue == null) return null;
            return String(issue).trim();
          }).filter(Boolean)
        : [];
      return {
        raw_country: input.raw_country == null ? null : String(input.raw_country),
        raw_region: input.raw_region == null ? null : String(input.raw_region),
        country: input.country == null ? null : String(input.country),
        region: input.region == null ? null : String(input.region),
        canonical_country_code: input.canonical_country_code == null ? null : String(input.canonical_country_code),
        canonical_subdivision_code:
          input.canonical_subdivision_code == null ? null : String(input.canonical_subdivision_code),
        geography_normalization_status: status == null ? null : String(status),
        geography_normalization_issues: normalizedIssues
      };
    }

    function extractGeographyFromResponse(body) {
      if (!body || typeof body !== "object") return null;
      var candidate = null;
      if (body.geography && typeof body.geography === "object") candidate = body.geography;
      else if (body.data && body.data.geography && typeof body.data.geography === "object") candidate = body.data.geography;
      else if (body.registration && body.registration.geography && typeof body.registration.geography === "object") {
        candidate = body.registration.geography;
      } else {
        candidate = body;
      }
      return normalizeBackendGeography(candidate);
    }

    function describeIssues(issues) {
      if (!issues || !issues.length) return "";
      return issues.join("; ");
    }

    function setHidden(name, val) {
      var el = form.elements[name];
      if (!el) {
        el = document.createElement("input");
        el.type = "hidden";
        el.name = name;
        form.appendChild(el);
      }
      el.value = val == null ? "" : String(val);
    }

    setHidden("event_id", stableEventId);
    if (!getHiddenValue("geography_normalization_issues")) {
      setGeographyState({ geography_normalization_issues: [] });
    }

    function updateResumeParam(token) {
      try {
        var url = new URL(window.location.href);
        if (token) {
          url.searchParams.set("resume", token);
        } else {
          url.searchParams.delete("resume");
        }
        window.history.replaceState({}, "", url.toString());
      } catch (err) {
        /* ignore */
      }
    }

    function storeResumeToken(token) {
      if (!token) return;
      resumeToken = token;
      autosaveArmed = true;
      safeSessionSetItem(storageKey, token);
      setHidden("resume_token", token);
      updateResumeParam(token);
    }

    function clearResumeToken() {
      resumeToken = null;
      autosaveArmed = false;
      safeSessionRemoveItem(storageKey);
      setHidden("resume_token", "");
      updateResumeParam(null);
      clearTimeout(autosaveTimer);
    }

    function armAutosave(event) {
      if (autosaveArmed) return;
      if (event && event.isTrusted === false) return;
      autosaveArmed = true;
    }

    function captureUTM() {
      firstTouchCache = getOrInitFirstTouch();
      var ft = firstTouchCache || {};
      ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"].forEach(function (k) {
        setHidden(k, ft[k] || "");
      });
      setHidden("landing_page", ft.landing_page || window.location.href);
      setHidden("referrer", ft.referrer || "");
      setHidden("traffic_channel", ft.traffic_channel || deriveTrafficChannel(ft, ft.referrer || ""));
      setHidden("first_touch_at", ft.first_touch_at || "");
      setHidden("device_type", ft.device_type || getDeviceType());
      setHidden("client_id", ft.client_id || getOrInitClientId());
      setHidden("event_id", stableEventId);
      setHidden("fbp", ft.fbp || "");
      setHidden("fbc", ft.fbc || "");
      CLICK_ID_KEYS.forEach(function (key) {
        setHidden(key, ft[key] || "");
      });
    }

    function ensureSession() {
      var t = safeGetItem("flair_reg_session");
      if (!t) {
        t = window.crypto && crypto.randomUUID ? crypto.randomUUID() : Date.now() + "-" + Math.random();
        safeSetItem("flair_reg_session", t);
      }
      setHidden("session_token", t);
      return t;
    }

    function syncAddressHidden() {
      if (addressInput && addressHidden) addressHidden.value = addressInput.value || "";
    }

    if (addressInput) {
      addressInput.addEventListener("input", syncAddressHidden);
      addressInput.addEventListener("change", syncAddressHidden);
      addressInput.style.setProperty("-webkit-text-security", "none", "important");
      addressInput.style.setProperty("text-security", "none", "important");
    }

    var textSecurityGuard = setInterval(function () {
      if (!document.body.contains(root)) {
        clearInterval(textSecurityGuard);
        return;
      }
      if (addressInput) {
        addressInput.style.setProperty("-webkit-text-security", "none", "important");
        addressInput.style.setProperty("text-security", "none", "important");
      }
    }, 800);

    var titles = [
      "Register Your Store",
      "Store Address",
      "Tell Us About Your Store",
      "Register Additional Team Members",
      "Registration Complete"
    ];

    function setProgress() {
      var displayTotal = Math.max(1, totalFormSteps);
      var isConfirmation = currentStepIndex >= confirmationStepIndex;
      var displayStep = isConfirmation ? displayTotal : Math.min(displayTotal, currentStepIndex + 1);
      var frac = isConfirmation ? 1 : displayStep / displayTotal;
      var pctValue = Math.max(0, Math.min(100, frac * 100));
      var pctString = pctValue + "%";

      if (rail) {
        rail.style.setProperty("--p", String(frac));
        rail.style.setProperty("--progress", pctString);
        rail.setAttribute("data-progress-frac", String(frac));
      }
      if (bar) {
        bar.style.setProperty("--p", String(frac));
        bar.style.setProperty("--progress", pctString);
        bar.style.width = pctString;
      }
      if (progressEl) progressEl.setAttribute("aria-valuenow", String(Math.round(pctValue)));
      if (stepLabelEl) {
        var titleIndex = Math.min(currentStepIndex, titles.length - 1);
        var visibleTitle = "";
        var titleEl = allSteps[currentStepIndex] && allSteps[currentStepIndex].querySelector(".flair-step__title");
        if (titleEl) visibleTitle = (titleEl.textContent || "").trim();
        stepLabelEl.innerHTML =
          "Step <strong>" + displayStep + "</strong> of <strong>" + displayTotal + "</strong> — " + (visibleTitle || titles[titleIndex] || "");
      }
    }

    function scrollStepIntoView() {
      var progressRect = root.querySelector(".flair-reg__progress");
      var scrollOffset = 60;
      var targetElement = progressRect || allSteps[currentStepIndex];
      var targetRect = targetElement.getBoundingClientRect();
      var targetTop = targetRect.top + window.pageYOffset - scrollOffset;
      var scrollOptions = { top: Math.max(0, targetTop), behavior: "smooth" };
      try {
        window.scrollTo(scrollOptions);
      } catch (_) {
        window.scrollTo(0, Math.max(0, targetTop));
      }
    }

    function focusFirstField() {
      var focusables = allSteps[currentStepIndex].querySelectorAll("input:not([type='hidden']), textarea, select, button");
      var first = null;
      for (var f = 0; f < focusables.length; f++) {
        var el = focusables[f];
        if (el.disabled) continue;
        if (el.tagName === "SELECT") continue;
        first = el;
        break;
      }
      if (!first && focusables.length) first = focusables[0];
      if (first && typeof first.focus === "function") {
        try {
          first.focus({ preventScroll: true });
        } catch (e) {
          first.focus();
        }
      }
    }

    function pushStepEvent(displayStep) {
      window.dataLayer = window.dataLayer || [];
      var stepEvent = {
        // Tracking only — safe rename; backend unaffected.
        event: "flair_retail_registration_step",
        step: displayStep,
        event_id: stableEventId,
        show_code: showCode || showSlug || "",
        program: "FLAIR",
        registration_type: "retailer"
      };
      var safeStep = sanitizeForDataLayer(stepEvent);
      if (safeStep) window.dataLayer.push(safeStep);
    }

    function trackStepOneInteraction(event) {
      if (stepOneTracked || !event || event.isTrusted !== true) return;
      stepOneTracked = true;
      pushStepEvent(1);
    }

    function showStep(index, options) {
      options = options || {};
      var idx = Math.max(0, Math.min(index, totalSteps - 1));
      for (var s = 0; s < allSteps.length; s++) allSteps[s].hidden = s !== idx;
      currentStepIndex = idx;

      var activeStep = allSteps[currentStepIndex];
      if (lazyPlacesEnabled && addressInput && activeStep && activeStep.contains(addressInput)) {
        requestLazyPlaces();
      }

      setProgress();
      if (options.scroll !== false) scrollStepIntoView();
      if (options.focus !== false) focusFirstField();

      var isConfirmation = currentStepIndex >= confirmationStepIndex;
      var displayStep = isConfirmation ? totalFormSteps : currentStepIndex + 1;

      if (!isConfirmation && displayStep > 1) pushStepEvent(displayStep);

      scrubTextSecurity(root);
      syncAddressHidden();
      if (currentStepIndex < confirmationStepIndex) scheduleAutosave("step");
    }

    function validateStep(index) {
      var fs = allSteps[index];
      var required = fs.querySelectorAll("[required]");
      for (var i = 0; i < required.length; i++) {
        var el = required[i];
        var bad = !el.value || (el.type === "email" && !/\S+@\S+\.\S+/.test(el.value));
        if (bad) {
          el.focus();
          el.setAttribute("aria-invalid", "true");
          setMessage("Please complete the required fields.", "error");
          return false;
        } else {
          el.removeAttribute("aria-invalid");
        }
      }
      setMessage("");
      return true;
    }

    function hasOthersYes() {
      var yesRadio = form.querySelector("input[name='has_others'][value='Yes']");
      return !!(yesRadio && yesRadio.checked);
    }

    function toggleOthersCount() {
      if (!othersCountWrap) return;
      var show = hasOthersYes();
      othersCountWrap.hidden = !show;
      othersCountWrap.setAttribute("aria-hidden", show ? "false" : "true");
      if (othersCountWrap.classList) othersCountWrap.classList.toggle("is-hidden", !show);
      if (!show && othersCountEl) othersCountEl.value = "0";
    }

    function renderAttendees() {
      if (!attendeesWrap) return;
      var n = 0;
      if (hasOthersYes() && othersCountEl) {
        var v = parseInt(othersCountEl.value || "0", 10);
        if (!isNaN(v)) n = Math.min(10, Math.max(0, v));
      }
      attendeesWrap.innerHTML = "";
      for (var i = 1; i <= n; i++) {
        var firstId = "attendee-" + i + "-first";
        var lastId = "attendee-" + i + "-last";
        var emailId = "attendee-" + i + "-email";

        var block = document.createElement("div");
        block.className = "attendee-block";
        block.setAttribute("data-guest-index", String(i));

        block.innerHTML =
          '<div class="f-grid">' +
          '<label class="f-field">' +
          "<span>First name</span>" +
          '<input id="' + firstId + '" name="attendee_' + i + '_first" placeholder="First name">' +
          "</label>" +
          '<label class="f-field">' +
          "<span>Last name</span>" +
          '<input id="' + lastId + '" name="attendee_' + i + '_last" placeholder="Last name">' +
          "</label>" +
          '<label class="f-field">' +
          "<span>Email</span>" +
          '<input id="' + emailId + '" name="attendee_' + i + '_email" type="email" inputmode="email" placeholder="email@domain.com">' +
          "</label>" +
          "</div>";

        attendeesWrap.appendChild(block);
      }
    }

    function getParam(name) {
      var m = new RegExp("[?&]" + name + "=([^&]+)").exec(window.location.search);
      return m ? decodeURIComponent(m[1].replace(/\+/g, " ")) : null;
    }

    function setField(name, val) {
      var el = form.elements[name];
      if (!el) return;
      if (el instanceof RadioNodeList) {
        var list = Array.prototype.slice.call(form.querySelectorAll('[name="' + name + '"]'));
        list.forEach(function (n) {
          if (n.type === "checkbox") {
            if (Array.isArray(val)) n.checked = val.indexOf(n.value) !== -1;
          } else if (n.type === "radio") {
            n.checked = String(n.value) === String(val);
          }
        });
        return;
      }
      if (el.tagName === "SELECT") el.value = val != null ? val : "";
      else if (el.type === "checkbox") el.checked = !!val;
      else el.value = val != null ? val : "";
    }

    function hydrateFromDraft(draft) {
      if (!draft) return;
      var payload = draft.payload || {};
      [
        "company",
        "email",
        "first_name",
        "last_name",
        "phone",
        "address1",
        "address2",
        "city",
        "postal_code",
        "store_phone",
        "store_category",
        "show_code",
        "show_slug",
        "others_count",
        "utm_source",
        "utm_medium",
        "utm_campaign",
        "utm_content",
        "utm_term"
      ].forEach(function (k) {
        if (payload[k] != null) setField(k, payload[k]);
      });

      if (Array.isArray(payload.categories)) {
        var boxes = form.querySelectorAll('input[name="categories[]"]');
        boxes.forEach(function (b) {
          b.checked = payload.categories.indexOf(b.value) !== -1;
        });
      }
      if (payload.raw_country != null) setField("country_display", payload.raw_country);
      if (payload.raw_region != null) setField("region_display", payload.raw_region);

      if (payload.has_others === "Yes" || payload.has_others === "No") {
        setField("has_others", payload.has_others);
      }

      if (Array.isArray(payload.guests) && payload.guests.length) {
        var guests = payload.guests;
        var n = Math.min(10, guests.length);
        setField("others_count", String(n));
        toggleOthersCount();
        renderAttendees();
        for (var i = 0; i < n; i++) {
          var g = guests[i] || {};
          var idx = g.guest_index || i + 1;
          var f = form.elements["attendee_" + idx + "_first"];
          var l = form.elements["attendee_" + idx + "_last"];
          var e = form.elements["attendee_" + idx + "_email"];
          if (f) f.value = g.first_name || "";
          if (l) l.value = g.last_name || "";
          if (e) e.value = g.email || "";
        }
      }

      var disp = form.querySelector('[name="address1_display"]');
      if (disp && !disp.value && payload.address1) disp.value = payload.address1;
      syncAddressHidden();
      var geoDraft = {};
      if (payload.raw_country != null) geoDraft.raw_country = payload.raw_country;
      if (payload.raw_region != null) geoDraft.raw_region = payload.raw_region;
      if (payload.country != null) geoDraft.country = payload.country;
      if (payload.region != null) geoDraft.region = payload.region;
      if (payload.canonical_country_code != null) geoDraft.canonical_country_code = payload.canonical_country_code;
      if (payload.canonical_subdivision_code != null) geoDraft.canonical_subdivision_code = payload.canonical_subdivision_code;
      if (payload.geography_normalization_status != null) geoDraft.geography_normalization_status = payload.geography_normalization_status;
      if (payload.geography_normalization_issues != null) {
        if (Array.isArray(payload.geography_normalization_issues)) geoDraft.geography_normalization_issues = payload.geography_normalization_issues;
        else if (typeof payload.geography_normalization_issues === "string") {
          try {
            var parsedIssues = JSON.parse(payload.geography_normalization_issues);
            if (Array.isArray(parsedIssues)) geoDraft.geography_normalization_issues = parsedIssues;
          } catch (err) {
            /* ignore */
          }
        }
      }
      if (Object.keys(geoDraft).length) setGeographyState(geoDraft);

      var stepIndex = typeof draft.step_index === "number" ? draft.step_index : 0;
      stepIndex = Math.max(0, Math.min(stepIndex, allSteps.length - 1));
      showStep(stepIndex);

      setMessage("We restored your saved progress.", "warning");
    }

    function scrubTextSecurity(scope) {
      if (!scope) return;
      var fields = scope.querySelectorAll(".f-field input, .f-field textarea");
      for (var i = 0; i < fields.length; i++) {
        try {
          fields[i].style.setProperty("-webkit-text-security", "none", "important");
          fields[i].style.setProperty("text-security", "none", "important");
        } catch (err) {
          /* no-op */
        }
      }
    }

    function waitForPlaces(cb) {
      var tries = 0;
      var iv = setInterval(function () {
        if (window.google && google.maps && google.maps.places) {
          clearInterval(iv);
          cb();
          return;
        }
        if (++tries > 100) clearInterval(iv);
      }, 100);
    }

    function attachPlaces() {
      if (!addressInput) return;
      var ac = new google.maps.places.Autocomplete(addressInput, {
        types: ["address"],
        fields: ["address_components", "formatted_address", "geometry"]
      });

      ac.addListener("place_changed", function () {
        var place = ac.getPlace();
        if (!place || !place.address_components) return;

        function part(type) {
          var comps = place.address_components || [];
          for (var i = 0; i < comps.length; i++) {
            if (comps[i].types && comps[i].types.indexOf(type) !== -1) {
              return comps[i].long_name || "";
            }
          }
          return "";
        }

        var streetNumber = part("street_number");
        var route = part("route");
        var address1 = (streetNumber ? streetNumber + " " : "") + (route || "");
        if (address1) addressInput.value = address1;
        syncAddressHidden();

        var cityEl = form.elements["city"];
        var regionEl = form.elements["region_display"];
        var zipEl = form.elements["postal_code"];
        var countryEl = form.elements["country_display"];

        if (cityEl) cityEl.value = part("locality") || part("sublocality") || part("postal_town") || "";
        if (regionEl) regionEl.value = part("administrative_area_level_1") || "";
        if (zipEl) zipEl.value = part("postal_code") || "";
        if (countryEl) countryEl.value = part("country") || "";
      });
    }

    function attachPlacesOnce() {
      if (placesAttachState !== "idle" || !hasGooglePlaces()) return;
      placesAttachState = "attaching";
      try {
        attachPlaces();
        placesAttachState = "attached";
      } catch (err) {
        placesAttachState = "failed";
        throw err;
      }
    }

    function removePlacesIntentListeners() {
      if (!placesIntentListenersBound) return;
      form.removeEventListener("pointerdown", handlePlacesIntent);
      form.removeEventListener("keydown", handlePlacesIntent);
      placesIntentListenersBound = false;
    }

    function requestLazyPlaces() {
      if (!lazyPlacesEnabled) return null;
      if (lazyPlacesPromise) return lazyPlacesPromise;

      removePlacesIntentListeners();
      lazyPlacesPromise = loadGooglePlacesOnce(lazyPlacesApiKey)
        .then(function () {
          attachPlacesOnce();
        })
        .catch(function (err) {
          console.warn("FLAIR address autocomplete unavailable", err);
        });
      return lazyPlacesPromise;
    }

    function handlePlacesIntent(event) {
      var stepOne = allSteps[0];
      if (!event || event.isTrusted !== true || !stepOne || !event.target || !stepOne.contains(event.target)) return;
      requestLazyPlaces();
    }

    function bindPlacesIntentListeners() {
      if (placesIntentListenersBound) return;
      form.addEventListener("pointerdown", handlePlacesIntent);
      form.addEventListener("keydown", handlePlacesIntent);
      placesIntentListenersBound = true;
    }

    /* -------- autosave helpers -------- */
    function serializePayload(options) {
      var forSubmit = !!(options && options.forSubmit);
      syncAddressHidden();
      captureRawGeographyFromVisible();

      var fd = new FormData(form);
      var payload = {};
      fd.forEach(function (value, key) {
        if (key === "categories[]") return;
        if (payload[key] !== undefined) {
          if (!Array.isArray(payload[key])) payload[key] = [payload[key]];
          payload[key].push(value);
        } else {
          payload[key] = value;
        }
      });
      var clientUuid = getOrInitClientId();
      var canonicalExternalId = chooseBestExternalId(
        latestExternalId,
        getCandidateExternalIds({
          shopifyId: shopifyCustomerId,
          clientId: clientUuid
        })
      );
      if (!canonicalExternalId) canonicalExternalId = getOrInitExternalId();
      latestExternalId = canonicalExternalId;

      var categories = fd
        .getAll("categories[]")
        .map(function (v) {
          return (v || "").toString().trim();
        })
        .filter(Boolean);
      if (categories.length) payload.categories = categories;

      var count = parseInt(payload.others_count || "0", 10);
      if (isNaN(count)) count = 0;
      count = Math.max(0, Math.min(count, 10));

      var guests = [];
      for (var i = 1; i <= count; i++) {
        var f = (payload["attendee_" + i + "_first"] || "").trim();
        var l = (payload["attendee_" + i + "_last"] || "").trim();
        var e = (payload["attendee_" + i + "_email"] || "").trim();
        var normalizedGuestEmail = normalizeEmail(e);
        if (f || l || e) {
          guests.push({ first_name: f || null, last_name: l || null, email: normalizedGuestEmail || null, guest_index: i });
        }
        delete payload["attendee_" + i + "_first"];
        delete payload["attendee_" + i + "_last"];
        delete payload["attendee_" + i + "_email"];
      }
      if (guests.length) payload.guests = guests;
      else delete payload.guests;

      payload.address1 = payload.address1 || payload.address1_display || "";
      delete payload.address1_display;

      payload.show_code = payload.show_code || showCode;
      if (!payload.show_slug && showSlug) payload.show_slug = showSlug;
      if (resumeToken) payload.resume_token = resumeToken;
      if (!payload.event_id) payload.event_id = stableEventId;
      var normalizedEmail = normalizeEmail(payload.email);
      payload.email = normalizedEmail || null;
      var geographyState = getGeographyState();
      payload.raw_country = geographyState.raw_country;
      payload.raw_region = geographyState.raw_region;
      payload.country = geographyState.country;
      payload.region = geographyState.region;
      payload.canonical_country_code = geographyState.canonical_country_code;
      payload.canonical_subdivision_code = geographyState.canonical_subdivision_code;
      payload.geography_normalization_status = geographyState.geography_normalization_status;
      payload.geography_normalization_issues = Array.isArray(geographyState.geography_normalization_issues)
        ? geographyState.geography_normalization_issues.slice()
        : [];
      delete payload.country_display;
      delete payload.region_display;

      var firstTouch = getOrInitFirstTouch() || {};
      payload.landing_page = payload.landing_page || firstTouch.landing_page || window.location.href;
      payload.referrer = payload.referrer || firstTouch.referrer || "";
      payload.traffic_channel = payload.traffic_channel || firstTouch.traffic_channel || deriveTrafficChannel(firstTouch, firstTouch.referrer || "");
      payload.first_touch_at = firstTouch.first_touch_at || payload.first_touch_at || "";
      payload.device_type = firstTouch.device_type || payload.device_type || getDeviceType();
      payload.utm_source = toNullableString(payload.utm_source || firstTouch.utm_source);
      payload.utm_medium = toNullableString(payload.utm_medium || firstTouch.utm_medium);
      payload.utm_campaign = toNullableString(payload.utm_campaign || firstTouch.utm_campaign);
      payload.utm_content = toNullableString(payload.utm_content || firstTouch.utm_content);
      payload.utm_term = toNullableString(payload.utm_term || firstTouch.utm_term);
      payload.client_id = firstTouch.client_id || payload.client_id || getOrInitClientId();
      var currentMeta = getMetaCookieValues(getQueryParams());
      payload.fbp = currentMeta.fbp || payload.fbp || firstTouch.fbp || null;
      payload.fbc = currentMeta.fbc || payload.fbc || firstTouch.fbc || null;
      CLICK_ID_KEYS.forEach(function (key) {
        if (!payload[key]) payload[key] = firstTouch[key] || "";
      });

      var utmObj = {
        utm_source: payload.utm_source,
        utm_medium: payload.utm_medium,
        utm_campaign: payload.utm_campaign,
        utm_content: payload.utm_content,
        utm_term: payload.utm_term
      };
      CLICK_ID_KEYS.forEach(function (key) {
        utmObj[key] = toNullableString(payload[key] || firstTouch[key]);
      });
      payload.utm = utmObj;

      var lastTouch = buildLastTouch();
      var extrasBase = ensureObject(payload.extras);
      var extras = Object.assign({}, extrasBase, lastTouch, {
        source: EXTRAS_SOURCE,
        landing_page: payload.landing_page || firstTouch.landing_page || window.location.href,
        referrer: toNullableString(payload.referrer || firstTouch.referrer),
        traffic_channel: payload.traffic_channel || firstTouch.traffic_channel || deriveTrafficChannel(firstTouch, firstTouch.referrer || ""),
        client_id: payload.client_id || firstTouch.client_id || getOrInitClientId(),
        session_token: payload.session_token || null,
        event_id: payload.event_id || stableEventId || null,
        fbp: payload.fbp || null,
        fbc: payload.fbc || null,
        first_touch_at: firstTouch.first_touch_at || null,
        utm_source: payload.utm_source || null,
        utm_medium: payload.utm_medium || null,
        utm_campaign: payload.utm_campaign || null,
        utm_content: payload.utm_content || null,
        utm_term: payload.utm_term || null
      });
      CLICK_ID_KEYS.forEach(function (key) {
        extras[key] = payload[key] || firstTouch[key] || null;
      });
      extras.external_id = canonicalExternalId || null;
      payload.extras = extras;

      if (forSubmit) {
        payload.submission_page = window.location.href;
        payload.extras.last_touch_at = payload.extras.last_touch_at || lastTouch.last_touch_at || new Date().toISOString();
      }

      return payload;
    }

    function currentProgressFraction() {
      if (!totalFormSteps) return null;
      if (currentStepIndex >= confirmationStepIndex) return 1;
      return Math.max(0, Math.min(1, (currentStepIndex + 1) / totalFormSteps));
    }

    function scheduleAutosave(reason) {
      if (submitting || !autosaveArmed || currentStepIndex >= confirmationStepIndex) return;
      clearTimeout(autosaveTimer);
      autosaveTimer = setTimeout(function () {
        autosaveNow(reason);
      }, AUTOSAVE_DELAY_MS);
    }

    function autosaveNow(reason) {
      if (submitting || !autosaveArmed || currentStepIndex >= confirmationStepIndex) return;
      var payload = serializePayload({ forSubmit: false });
      var signature = safeStringify({ payload: payload, step_index: currentStepIndex });
      if (signature && signature === lastAutosaveSignature) return;
      lastAutosaveSignature = signature;

      var body = {
        show_code: payload.show_code || payload.show_slug || "",
        email: payload.email || null,
        step: currentStepIndex + 1,
        progress: currentProgressFraction(),
        event_id: payload.event_id || stableEventId || null,
        resume_token: resumeToken,
        guest_count: Array.isArray(payload.guests) ? payload.guests.length : null,
        utm: payload.utm || null,
        extras: payload.extras || null,
        data: {
          payload: payload,
          step_index: currentStepIndex,
          reason: reason || null
        }
      };

      if (!body.show_code) return;

      fetch(apiBase + "/api/registration-session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      })
        .then(safeReadJson)
        .then(function (json) {
          if (json && json.ok && json.resume_token) storeResumeToken(json.resume_token);
          var autosaveGeography = extractGeographyFromResponse((json && json.session) || json);
          if (autosaveGeography) setGeographyState(autosaveGeography);
        })
        .catch(function (err) {
          console.warn("FLAIR autosave failed", err);
        });
    }

    function normalizeDraftData(session) {
      if (!session) return null;
      var raw = session.data || session;
      if (typeof raw === "string") {
        try {
          raw = JSON.parse(raw);
        } catch (err) {
          raw = null;
        }
      }
      if (!raw || typeof raw !== "object") return null;
      if (raw.payload) return raw;
      return { payload: raw, step_index: raw.step_index || 0 };
    }

    function loadDraft(token) {
      if (!token) return Promise.resolve();
      return fetch(apiBase + "/api/registration-session?token=" + encodeURIComponent(token), {
        method: "GET",
        headers: { Accept: "application/json" }
      })
        .then(safeReadJson)
        .then(function (json) {
          if (!json || !json.ok || !json.session) return;
          storeResumeToken(json.session.resume_token || token);
          var draft = normalizeDraftData(json.session);
          hydrateFromDraft(draft);
          lastAutosaveSignature = "";
          scheduleAutosave("hydrate");
        })
        .catch(function (err) {
          console.warn("FLAIR resume load failed", err);
        });
    }

  function finalizeSession() {
    if (!resumeToken) return Promise.resolve();
    return fetch(apiBase + "/api/registration-session/" + encodeURIComponent(resumeToken) + "/finalize", {
      method: "POST",
      headers: { "Content-Type": "application/json" }
    })
      .then(safeReadJson)
      .catch(function (err) {
        console.warn("FLAIR finalize failed", err);
      });
  }

    /* -------- events -------- */
    form.addEventListener("keydown", function (e) {
      if (e.key !== "Enter" || e.defaultPrevented) return;
      var target = e.target;
      if (!target) return;
      var tag = target.tagName;
      if (tag === "TEXTAREA" || target.isContentEditable) return;
      if (tag === "BUTTON") return;
      if (tag === "INPUT") {
        var type = (target.type || "").toLowerCase();
        if (type === "submit" || type === "button") return;
      }
      var currentStep = allSteps[currentStepIndex];
      var hasNextButton = currentStep && currentStep.querySelector("[data-next]");
      if (hasNextButton && currentStepIndex < totalSteps - 1) {
        e.preventDefault();
        if (!validateStep(currentStepIndex)) return;
        showStep(currentStepIndex + 1);
      }
    });

    form.addEventListener("click", function (e) {
      var next = e.target.closest && e.target.closest("[data-next]");
      var prev = e.target.closest && e.target.closest("[data-prev]");
      if (next) {
        if (!validateStep(currentStepIndex)) return;
        if (currentStepIndex === 0) {
          var nowIso = new Date().toISOString();
          var mo = form.elements["marketing_opt_in"];
          var cv = form.elements["consent_version"];
          var ca = form.elements["consented_at"];
          if (mo) mo.value = "true";
          if (cv && !cv.value) cv.value = "flair-reg-v1";
          if (ca) ca.value = nowIso;
        }
        if (currentStepIndex < totalSteps - 1) showStep(currentStepIndex + 1);
      }
      if (prev) {
        if (currentStepIndex > 0) showStep(currentStepIndex - 1);
      }
    });

    for (var r = 0; r < othersToggle.length; r++) {
      othersToggle[r].addEventListener("change", function (event) {
        armAutosave(event);
        toggleOthersCount();
        if (hasOthersYes()) renderAttendees();
        else if (attendeesWrap) attendeesWrap.innerHTML = "";
        scheduleAutosave("toggle_guests");
      });
    }

    if (othersCountEl) {
      othersCountEl.addEventListener("input", function (event) {
        armAutosave(event);
        renderAttendees();
        scheduleAutosave("guest_count");
      });
    }

    form.addEventListener("input", function (event) {
      trackStepOneInteraction(event);
      armAutosave(event);
      scheduleAutosave("input");
    });

    form.addEventListener("change", function (event) {
      trackStepOneInteraction(event);
      armAutosave(event);
      scheduleAutosave("change");
    });

    form.addEventListener("submit", function (e) {
      e.preventDefault();
      if (!validateStep(currentStepIndex)) return;

      submitting = true;
      clearTimeout(autosaveTimer);

      var payload = serializePayload({ forSubmit: true });
      var submitEventId = stableEventId;
      payload.event_id = submitEventId;
      setHidden("event_id", submitEventId);
      var params = new URLSearchParams(location.search);
      if (!payload.show_code) {
        payload.show_code = params.get("show") || showCode || showSlug || "";
      }
      if (!payload.show_code && !payload.show_slug) {
        setMessage("Missing show code — configure it in the section settings.", "error");
        submitting = false;
        return;
      }
      if (resumeToken) payload.resume_token = resumeToken;

      if (debugAttribution) {
        console.log("[FLAIR] first_touch", firstTouchCache || getOrInitFirstTouch());
        console.log("[FLAIR] last_touch", lastTouchSnapshot || {});
        console.log("[FLAIR] payload", payload);
      }

      if (submitBtn) submitBtn.disabled = true;

      fetch(apiBase + "/api/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      })
        .then(safeReadJson)
        .then(function (body) {
          if (!body || body.ok === false) {
            var errMsg = body && body.error ? body.error : "Registration failed";
            var stepMsg = body && body.step ? " [" + body.step + "]" : "";
            setMessage("Registration failed: " + errMsg + stepMsg, "error");
            throw new Error(errMsg);
          }

          if (debugAttribution) console.log("DEBUG FLAIR: success handler reached", { payload: payload });
          var unknownWarningActive = false;
          var backendGeography = extractGeographyFromResponse(body);
          if (backendGeography) setGeographyState(backendGeography);
          var latestGeography = getGeographyState();
          payload.raw_country = latestGeography.raw_country;
          payload.raw_region = latestGeography.raw_region;
          payload.country = latestGeography.country;
          payload.region = latestGeography.region;
          payload.canonical_country_code = latestGeography.canonical_country_code;
          payload.canonical_subdivision_code = latestGeography.canonical_subdivision_code;
          payload.geography_normalization_status = latestGeography.geography_normalization_status;
          payload.geography_normalization_issues = latestGeography.geography_normalization_issues;
          if (latestGeography.geography_normalization_status === "conflict") {
            var conflictIssues = describeIssues(latestGeography.geography_normalization_issues);
            var conflictMsg = "Please correct your address before continuing.";
            if (conflictIssues) conflictMsg += " (" + conflictIssues + ")";
            setMessage(conflictMsg, "error");
            if (submitBtn) submitBtn.disabled = false;
            submitting = false;
            throw new Error("Geography conflict");
          }
          if (
            latestGeography.geography_normalization_status === "unknown" &&
            latestGeography.geography_normalization_issues.length
          ) {
            unknownWarningActive = true;
            setMessage(
              "We submitted your registration, but please review: " + describeIssues(latestGeography.geography_normalization_issues),
              "warning"
            );
          }

          // ----- Tracking: completed registration -----
          var showCodeForTracking = payload.show_code || payload.show_slug || "";
          var registrationId =
            body.registration_id ||
            body.id ||
            (body.data && (body.data.registration_id || body.data.id)) ||
            (body.registration && (body.registration.registration_id || body.registration.id)) ||
            null;
          var firedKey = "flair_retail_registration_success_" + (registrationId || stableEventId);
          try {
            if (sessionStorage.getItem(firedKey) === "1") return finalizeSession();
            sessionStorage.setItem(firedKey, "1");
          } catch (e) {
            /* ignore */
          }
          var program = "FLAIR";
          var registration_type = "retailer";
          var marketingConsented = marketingConsentGranted();
          var externalId =
            (payload.extras && payload.extras.external_id) || getCanonicalExternalId() || getOrInitExternalId();
          var firstTouchForIdentity = firstTouchCache || getOrInitFirstTouch();
          var lastTouchForIdentity = lastTouchSnapshot || buildLastTouch();
          var upgradeCandidates = getCandidateExternalIds({
            shopifyId: shopifyCustomerId,
            body: body || {},
            registrationId: registrationId,
            clientId: payload.client_id || getOrInitClientId()
          });
          var upgradedId = chooseBestExternalId(externalId, upgradeCandidates);
          if (upgradedId && upgradedId !== externalId && externalIdStrength(upgradedId) > externalIdStrength(externalId)) {
            externalId = upgradedId;
            latestExternalId = upgradedId;
            persistExternalId(externalId);
          }
          updateIdentitySnapshot(externalId, firstTouchForIdentity, lastTouchForIdentity);
          if (payload.extras) payload.extras.external_id = externalId;

          return prepareHashedIdentityData(payload).then(function (identityData) {
            var hashedData = (identityData && identityData.hashes) || {};
            var hashedFields = ["em", "ph", "fn", "ln", "ct", "st", "zp", "country"];
            var clickIdValues = {};
            CLICK_ID_KEYS.forEach(function (key) {
              clickIdValues[key] = payload[key] || (payload.extras && payload.extras[key]) || "";
            });

            if (marketingConsented) {
              hashedFields.forEach(function (field) {
                var hashVal = hashedData[field];
                if (hashVal) setCookie(field, hashVal, ONE_YEAR_SECONDS);
              });
              CLICK_ID_KEYS.forEach(function (key) {
                var clickVal = clickIdValues[key];
                if (clickVal) setCookie(key, clickVal, NINETY_DAYS_SECONDS);
              });
            }

            window.dataLayer = window.dataLayer || [];
            var successEvent = {
              // Tracking only — safe rename; backend unaffected.
              event: "flair_retail_registration_success",
              event_id: submitEventId,
              registration_id: registrationId,
              program: program,
              registration_type: registration_type,
              show_code: showCodeForTracking,
              page_path: window.location.pathname,
              traffic_channel: payload.traffic_channel || null,
              utm_source: payload.utm_source || null,
              utm_medium: payload.utm_medium || null,
              utm_campaign: payload.utm_campaign || null,
              utm_content: payload.utm_content || null,
              utm_term: payload.utm_term || null,
              fbp: payload.fbp || null,
              fbc: payload.fbc || null,
              client_id: payload.client_id || null,
              session_token: payload.session_token || null,
              external_id: externalId || null,
              meta_standard_event: "CompleteRegistration",
              google_conversion_event: "purchase_or_registration_complete",
              em: hashedData.em || null,
              ph: hashedData.ph || null,
              fn: hashedData.fn || null,
              ln: hashedData.ln || null,
              ct: hashedData.ct || null,
              st: hashedData.st || null,
              zp: hashedData.zp || null,
              country: hashedData.country || null
            };
            CLICK_ID_KEYS.forEach(function (key) {
              successEvent[key] = clickIdValues[key] || null;
            });
            var safeSuccess = sanitizeForDataLayer(successEvent);
            if (safeSuccess) window.dataLayer.push(safeSuccess);

            var standardEvent = {
              event: "flair_standard_complete_registration",
              event_id: submitEventId,
              registration_id: registrationId,
              show_code: showCodeForTracking,
              external_id: externalId || null,
              client_id: payload.client_id || null,
              fbp: payload.fbp || null,
              fbc: payload.fbc || null
            };
            CLICK_ID_KEYS.forEach(function (key) {
              standardEvent[key] = clickIdValues[key] || null;
            });
            var safeStandard = sanitizeForDataLayer(standardEvent);
            if (safeStandard) window.dataLayer.push(safeStandard);

            // 2) Generic submit event (same event_id, no PII at top level)
            var submitEvent = {
              // Tracking only — safe rename; backend unaffected.
              event: "flair_retail_registration_submit",
              event_id: submitEventId,
              lead_type: "buyer_registration",
              lead_origin: "shopify_registration_section",
              show_code: showCodeForTracking,
              registration_id: registrationId,
              program: program,
              registration_type: registration_type
            };
            var safeSubmit = sanitizeForDataLayer(submitEvent);
            if (safeSubmit) window.dataLayer.push(safeSubmit);

            // ----- Existing UX behavior -----
            if (!unknownWarningActive) setMessage("");
            if (root) root.setAttribute("data-complete", "true");
            showStep(totalSteps - 1);
            clearResumeToken();
            lastAutosaveSignature = "";
            return finalizeSession();
          });
        })
        .catch(function (err) {
          var hasMessage = !!(msgEl && msgEl.textContent);
          if (!hasMessage) setMessage("Network error submitting registration.", "error");
          console.error(err);
        })
        .finally(function () {
          if (submitBtn) submitBtn.disabled = false;
          submitting = false;
        });
    });

    /* -------- init -------- */
    captureUTM();
    ensureSession();
    scrubTextSecurity(root);
    toggleOthersCount();
    renderAttendees();
    syncAddressHidden();

    var resumeParam = getParam("resume");
    if (resumeParam) storeResumeToken(resumeParam);
    else if (resumeToken) setHidden("resume_token", resumeToken);

    if (resumeToken) {
      loadDraft(resumeToken).finally(function () {
        if (!resumeToken) showStep(0, { scroll: false, focus: false });
      });
    } else {
      showStep(0, { scroll: false, focus: false });
    }

    if (addressInput && lazyPlacesEnabled) bindPlacesIntentListeners();
    else if (addressInput) waitForPlaces(attachPlacesOnce);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      boot(document);
    });
  } else {
    boot(document);
  }

  document.addEventListener("shopify:section:load", function (e) {
    boot(e.target);
  });
})();
